/**
 * The image on a card.
 *
 * A board and a gallery both draw cards and both store the same setting, so
 * this is one menu offered to two view types. Unlike everything else in this
 * group the setting is optional: a card with no cover is a card, which is why
 * the two views have no "unconfigured" state about it.
 */
import { Image } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field, ViewOf } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { fileFields } from '../views/column-choices'
import { FieldChoice } from './field-choice'
import { useViewSettings } from './view-settings-mutations'

export function CardCoverMenu({
  tableId,
  view,
  fields,
}: {
  tableId: number
  view: ViewOf<'board'> | ViewOf<'gallery'>
  fields: readonly Field[]
}) {
  const { t } = useTranslation()
  const { choose } = useViewSettings(tableId, view.id)
  const candidates = fileFields(fields)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <Image className="size-4 opacity-70" />
          {t('view.cover')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {candidates.length === 0 ? (
          <DropdownMenuItem disabled>{t('view.coverNoFields')}</DropdownMenuItem>
        ) : (
          <FieldChoice
            label={t('view.coverField')}
            chosen={view.card_cover_image_field ?? null}
            candidates={candidates}
            onChoose={(id) => {
              choose({ card_cover_image_field: id })
            }}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
